import Header from "@components/header/Header";
import PDFViewer from "@components/pdfviewer/PDFViewer";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import styles from "./EvaluationPage.module.css";
import backIcon from "@assets/icons/back-arrow.svg";
import { setComponentToShow } from "@store/ModalToShow";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import QuestionRubricSolution from "@components/questionrubricsolution/QuestionRubricSolution";
import {
  EvaluationContainer,
  EvaluationFooter,
  QuestionsCarousel,
} from "@components/evaluation";
import type { Question } from "@services/evaluationService";
import AssignNextTaskConfirmation from "@modals/assignnexttaskconfirmation/AssignNextTaskConfirmation";
import { toast } from "sonner";

// Server State Hooks
import {
  useAssignEvaluationTaskByPhase as useAssignEvaluationTaskByPhaseQuery,
  useAssignedEvaluationTaskV3,
  useEvaluationMutations,
  useL1ReportSummaryNavigation,
  REPORT_SUMMARY_INDEX,
} from "@hooks/evaluation";
import {
  evaluationKeys,
  useAssignEvaluationTaskByPhase as useAssignTaskByPhaseMutation,
} from "@services/evaluationService";
import { useQueryClient } from "@tanstack/react-query";
import ReportSummary from "@components/reportsummary/ReportSummary";
import { type AttemptStatus } from "@constants/evaluationConstants";
import {
  isHideSingleQuestionConfirmationEnabled,
  isSingleQuestionTask,
  setHideSingleQuestionConfirmation,
} from "@utils/evaluationUtilities";
import ReportError from "@modals/reporterror/ReportError";
import ConfirmAction from "@modals/confirmaction/ConfirmAction";
import { useEvaluationFeatures } from "@hooks/store";

const INCOMPLETE_EVALUATION_MESSAGE =
  "Please complete the evaluation before proceeding.";

function EvaluationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const evaluationFeatures = useEvaluationFeatures();
  const showReportError = evaluationFeatures.reportError;
  const showL1EvaluationReport = evaluationFeatures.l1EvaluationReport;
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [isAssigningNextTask, setIsAssigningNextTask] = useState(false);

  // Get evaluation_group_id from URL query parameters
  const evaluationGroupIdFromUrl = searchParams.get("evaluation_group_id") ?? undefined;
  const evaluationPhaseIdFromUrl =
    searchParams.get("evaluation_phase_id") ?? undefined;

  // Determine mode from router state
  const routerState = location.state as
    | { mode: "resume"; assignmentId: string; forceNewTask?: boolean }
    | {
        mode: "assign";
        evaluationGroupId?: string;
        evaluationPhaseId?: string;
        forceNewTask?: boolean;
      }
    | null
    | undefined;

  const evaluationPhaseId =
    routerState?.mode === "assign"
      ? routerState.evaluationPhaseId ?? evaluationPhaseIdFromUrl
      : evaluationPhaseIdFromUrl;

  // Use appropriate hook based on mode
  const isResumeMode = routerState?.mode === "resume" && !routerState?.forceNewTask;
  const isAssignMode =
    routerState?.mode === "assign" ||
    routerState?.forceNewTask ||
    Boolean(evaluationPhaseIdFromUrl);
  const isPhaseAssignMode = isAssignMode && Boolean(evaluationPhaseId);
  const isDirectNavigation = !routerState; // No router state = direct navigation

  const shouldAssignByPhase =
    isPhaseAssignMode &&
    (Boolean(routerState?.forceNewTask) || isDirectNavigation);

  const shouldFetchAssignedTask =
    isResumeMode ||
    (isPhaseAssignMode && !shouldAssignByPhase) ||
    (isDirectNavigation && !isPhaseAssignMode);

  const {
    task: v3AssignedTask,
    questions: v3AssignedQuestions,
    isLoading: isV3AssignedLoading,
    isFetching: isV3AssignedFetching,
    reportIssueContext: assignedReportIssueContext,
  } = useAssignedEvaluationTaskV3(shouldFetchAssignedTask);

  const {
    task: phaseAssignedTask,
    questions: phaseAssignedQuestions,
    isLoading: isPhaseAssignLoading,
    reportIssueContext: phaseAssignReportIssueContext,
  } = useAssignEvaluationTaskByPhaseQuery(
    evaluationPhaseId,
    shouldAssignByPhase
  );

  const task = shouldAssignByPhase ? phaseAssignedTask : v3AssignedTask;
  const questions = shouldAssignByPhase
    ? phaseAssignedQuestions
    : v3AssignedQuestions;
  const isLoading = shouldAssignByPhase
    ? isPhaseAssignLoading
    : isV3AssignedLoading;
  const reportIssueContext = shouldAssignByPhase
    ? phaseAssignReportIssueContext
    : assignedReportIssueContext;

  const {
    saveEvaluation,
    isSaving,
    submitEvaluation,
    isSubmitting,
    reportIssue,
    isReportingIssue,
  } = useEvaluationMutations();

  const { mutateAsync: assignTaskByPhase } = useAssignTaskByPhaseMutation();

  // Redirect to dashboard if no valid mode is provided AND no assigned task found
  useEffect(() => {
    if (
      isDirectNavigation &&
      !isV3AssignedLoading &&
      !v3AssignedTask &&
      !isLoading
    ) {
      navigate("/dashboard", {
        state: {
          message: "Please select an evaluation task from the dashboard.",
        },
      });
    }
  }, [
    isDirectNavigation,
    isV3AssignedLoading,
    v3AssignedTask,
    isLoading,
    navigate,
  ]);

  // Redirect if no task is available after loading completes
  useEffect(() => {
    if (
      !isLoading &&
      !isV3AssignedFetching &&
      !isSubmitting &&
      !isAssigningNextTask &&
      (!task || !questions.length)
    ) {
      navigate("/dashboard", {
        state: {
          message:
            "No evaluation task found. Please select a task from the dashboard.",
        },
      });
    }
  }, [
    isLoading,
    isV3AssignedFetching,
    isSubmitting,
    isAssigningNextTask,
    task,
    questions.length,
    navigate,
  ]);

  const {
    isReportSummaryEnabled,
    isReportView,
    questionIndex,
    currentQuestion,
    handleQuestionSelect,
    goToPrevQuestion,
    goToNextQuestion,
    goToNextFromReport,
  } = useL1ReportSummaryNavigation(
    questions,
    showL1EvaluationReport,
    task?.assignmentId ?? task?.id
  );
  const solutionPdf = task?.submission_url ?? null;

  const [evaluationData, setEvaluationData] = useState<{
    awarded_marks: number;
    feedbacks: string[];
    attempt_status: AttemptStatus;
  } | null>(null);

  const [approvalValid, setApprovalValid] = useState(true);
  const [activeTab, setActiveTab] = useState<
    "question" | "rubrics" | "solution" | ""
  >("question");
  const token = localStorage.getItem("accessToken") || "";

  useEffect(() => {
    setActiveTab("question");
  }, [task?.assignmentId, task?.id]);

  useEffect(() => {
    if (!currentQuestion) return;

    // Reset approval validity when question changes
    const question = currentQuestion;

    // Approval is only valid if:
    // 1. It is L1Evaluated AND has NO student comment AND has a current_evaluation (meaning it was accepted or overridden)
    // 2. OR it is StudentChallenged AND has current_evaluation (meaning it was resolved)
    // Simplified: It must have a current_evaluation to be valid (since L1 acceptance now sets current_evaluation)
    setApprovalValid(question.current_evaluation !== null);

    // Reset evaluationData when question changes - EvaluationContainer will populate it
    // if current_evaluation exists, or when user makes changes
    setEvaluationData(null);
  }, [currentQuestion]);

  const componentName = useSelector(
    (state: any) => state.modalToShow.componentName
  );

  const handleCloseModal = () => {
    dispatch(setComponentToShow(""));
  };

  const isCurrentEvaluationComplete = () =>
    Boolean(
      currentQuestion &&
        (evaluationData || currentQuestion.current_evaluation),
    );

  const ensureEvaluationComplete = (): boolean => {
    if (!currentQuestion) return false;
    if (isCurrentEvaluationComplete()) return true;
    toast.error(INCOMPLETE_EVALUATION_MESSAGE);
    return false;
  };

  const proceedWithSubmission = async () => {
    if (!currentQuestion || !evaluationData || !task) return false;

    try {
      await saveEvaluation(task.assignmentId, {
        question_id: currentQuestion.question_id,
        awarded_marks: evaluationData.awarded_marks,
        feedbacks: evaluationData.feedbacks,
        attempt_status: evaluationData.attempt_status,
      });
      return true;
    } catch {
      return false;
    }
  };

  const proceedWithSubmitEvaluation = async (assignmentId: string) => {
    try {
      await submitEvaluation(assignmentId);
      return true;
    } catch {
      return false;
    }
  };

  const saveEvaluationBeforeFinalize = async (): Promise<boolean> => {
    if (!currentQuestion || !task) return false;

    if (evaluationData) {
      return proceedWithSubmission();
    }

    if (!currentQuestion.current_evaluation) {
      toast.error(INCOMPLETE_EVALUATION_MESSAGE);
      return false;
    }

    return true;
  };

  const finalizeAndSubmitEvaluation = async (): Promise<boolean> => {
    const isReady = await saveEvaluationBeforeFinalize();
    if (!isReady || !task) return false;
    return proceedWithSubmitEvaluation(task.assignmentId);
  };

  const goBackToDashboard = () => {
    navigate("/dashboard");
  };

  const handleApproveAndNext = async () => {
    if (!ensureEvaluationComplete()) return;

    if (evaluationData) {
      const success = await proceedWithSubmission();
      if (success) goToNextQuestion();
      return;
    }

    goToNextQuestion();
  };

  const handleApproveAndSubmit = () => {
    if (!currentQuestion || !task) return;

    // Check if all previous questions have been evaluated
    const previousQuestions = questions.slice(0, questionIndex);
    const unevaluatedQuestions = previousQuestions.filter(
      (q) => q.current_evaluation === null
    );
    if (unevaluatedQuestions.length > 0) {
      toast.info(
        "Please complete evaluation of all previous remaining questions before submitting.",
        {
          duration: 5 * 1000, // 10 seconds
        }
      );
      return;
    }

    dispatch(setComponentToShow("AssignNextTaskConfirmation"));
  };

  const handleSubmitAndGoBack = async () => {
    dispatch(setComponentToShow(""));

    const submitSuccess = await finalizeAndSubmitEvaluation();
    if (submitSuccess) {
      navigate("/dashboard");
    }
  };

  const handleAssignNextTask = async () => {
    dispatch(setComponentToShow(""));

    const phaseId =
      evaluationPhaseId ??
      task?.evaluation_phase_id ??
      evaluationPhaseIdFromUrl;

    if (!phaseId) {
      toast.error("Evaluation phase is not available to assign the next task.");
      navigate("/dashboard");
      return;
    }

    setIsAssigningNextTask(true);

    try {
      const submitSuccess = await finalizeAndSubmitEvaluation();
      if (!submitSuccess) {
        setIsAssigningNextTask(false);
        return;
      }

      await queryClient.cancelQueries({ queryKey: evaluationKeys.assignedV3() });

      const nextTask = await assignTaskByPhase(phaseId);
      if (!nextTask) {
        toast.error("No evaluation task available for this phase.");
        queryClient.setQueryData(evaluationKeys.assignedV3(), null);
        setIsAssigningNextTask(false);
        navigate("/dashboard");
        return;
      }

      queryClient.setQueryData(evaluationKeys.assignedV3(), nextTask);

      const groupId =
        nextTask.evaluation_group_id ??
        evaluationGroupIdFromUrl ??
        task?.evaluation_group_id;

      navigate(
        `/evaluation?evaluation_group_id=${groupId}&evaluation_phase_id=${phaseId}`,
        {
          replace: true,
          state: {
            mode: "assign",
            evaluationGroupId: groupId,
            evaluationPhaseId: phaseId,
          },
        }
      );
    } catch {
      toast.error("Failed to assign the next evaluation task.");
      setIsAssigningNextTask(false);
      navigate("/dashboard");
    }
  };

  const handleSingleQuestionApproveAndNext = () => {
    if (!ensureEvaluationComplete()) return;

    if (isHideSingleQuestionConfirmationEnabled()) {
      void handleAssignNextTask();
      return;
    }

    dispatch(setComponentToShow("ConfirmAction"));
  };

  const handleConfirmProceed = (dontShowAgain: boolean) => {
    setHideSingleQuestionConfirmation(dontShowAgain);
    void handleAssignNextTask();
  };

  const handleReportError = () => {
    if (!showReportError || !reportIssueContext) {
      if (showReportError && !reportIssueContext) {
        toast.error("Task details are not available to report an issue.");
      }
      return;
    }
    dispatch(setComponentToShow("ReportError"));
  };

  const handleReportIssueSubmit = async ({
    pageNumbers,
    reason,
  }: {
    pageNumbers: string;
    reason: string;
  }) => {
    if (!reportIssueContext) return;

    await reportIssue({
      studentId: reportIssueContext.studentId,
      examId: reportIssueContext.examId,
      taskId: reportIssueContext.taskId,
      pageNumbers,
      reason,
    });
  };

  const renderModal = () => {
    const modalProps = { isOpen: true, onClose: handleCloseModal };

    switch (componentName) {
      case "AssignNextTaskConfirmation":
        return (
          <AssignNextTaskConfirmation
            {...modalProps}
            onAssignNextTask={handleAssignNextTask}
            onSubmitAndGoBack={handleSubmitAndGoBack}
          />
        );
      case "ReportError":
        return showReportError && reportIssueContext ? (
          <ReportError
            {...modalProps}
            onSubmit={handleReportIssueSubmit}
            isSubmitting={isReportingIssue}
          />
        ) : null;
      case "ConfirmAction":
        return (
          <ConfirmAction {...modalProps} onProceed={handleConfirmProceed} />
        );
      default:
        return null;
    }
  };
  if (isLoading) {
    return <div className={styles.loadingContainer}>Loading...</div>;
  }

  // Show loading state while redirecting
  if (!task || !questions.length) {
    return <div className={styles.loadingContainer}>No task available...</div>;
  }

  const singleQuestionTask = isSingleQuestionTask(questions.length);
  const showQuestionsCarousel = !singleQuestionTask;

  const footerHandlers = singleQuestionTask
    ? {
        onApproveAndSubmit: handleSubmitAndGoBack,
        onApproveAndNext: handleSingleQuestionApproveAndNext,
      }
    : {
        onApproveAndSubmit: handleApproveAndSubmit,
        onApproveAndNext: handleApproveAndNext,
      };

  return (
    <>
      <Header />
      <div className={styles.evaluatorPage}>
        <div className={styles.evaluatorHeader}>
          <button className={styles.backBtn} onClick={goBackToDashboard}>
            <img src={backIcon} /> Back to Dashboard
          </button>
          <div className={styles.testInfo}>
            {/* <h2>Maths Grade-9 Unit-1 Test - 15th May 2025</h2> */}
            {/* <p>Admission ID:{"N/A"}</p> */}
            {showReportError && (
              <button
                className={styles.reportErrorBtn}
                onClick={handleReportError}
              >
                Report Error
              </button>
            )}
          </div>
        </div>
        <div className={styles.mainContainer}>
          {showQuestionsCarousel && (
            <QuestionsCarousel
              questions={questions}
              questionIndex={questionIndex}
              onQuestionSelect={handleQuestionSelect}
              showReportSummaryNav={isReportSummaryEnabled}
              reportSummaryIndex={REPORT_SUMMARY_INDEX}
            />
          )}

          <div className={styles.evaluatorMainContent}>
            {showL1EvaluationReport && isReportView ? (
              <div className={styles.reportSummaryWrapper}>
                <ReportSummary questions={questions} />
              </div>
            ) : (
              <>
            <div className={styles.leftSidePanel}>
              <div className={styles.tabBar}>
                <button
                  className={styles.tab}
                  onClick={() =>
                    setActiveTab((prev) =>
                      prev === "question" ? "" : "question"
                    )
                  }
                >
                  <span className={styles.tabLabel}>View Question</span>
                  {activeTab === "question" && (
                    <motion.div
                      layoutId="activeTab"
                      className={styles.activeTabIndicator}
                      transition={{
                        type: "spring",
                        bounce: 0.2,
                        duration: 0.6,
                      }}
                    />
                  )}
                </button>
                <button
                  className={styles.tab}
                  onClick={() =>
                    setActiveTab((prev) => (prev === "rubrics" ? "" : "rubrics"))
                  }
                >
                  <span className={styles.tabLabel}>View Rubric</span>
                  {activeTab === "rubrics" && (
                    <motion.div
                      layoutId="activeTab"
                      className={styles.activeTabIndicator}
                      transition={{
                        type: "spring",
                        bounce: 0.2,
                        duration: 0.6,
                      }}
                    />
                  )}
                </button>
                <button
                  className={styles.tab}
                  onClick={() =>
                    setActiveTab((prev) =>
                      prev === "solution" ? "" : "solution"
                    )
                  }
                >
                  <span className={styles.tabLabel}>View Solution</span>
                  {activeTab === "solution" && (
                    <motion.div
                      layoutId="activeTab"
                      className={styles.activeTabIndicator}
                      transition={{
                        type: "spring",
                        bounce: 0.2,
                        duration: 0.6,
                      }}
                    />
                  )}
                </button>
              </div>
              <AnimatePresence mode="wait">
                {activeTab && (
                  <motion.div
                    key={`${questionIndex}-${activeTab}`}
                    className={styles.slideContainer}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{
                      height: "auto",
                      opacity: 1,
                    }}
                    exit={{
                      height: 0,
                      opacity: 0,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 25,
                      mass: 0.8,
                    }}
                  >
                    <p className={styles.sectionTitle}>
                      {activeTab === "question"
                        ? `Question`
                        : activeTab === "rubrics"
                        ? "Rubric"
                        : "Solution"}
                    </p>
                    <div className={styles.questionSection}>
                      {currentQuestion && (
                        <QuestionRubricSolution
                          activeTab={activeTab}
                          question={currentQuestion}
                          token={token}
                        />
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <p>Student Answer</p>
              <div className={styles.studentPdfSection}>
                {solutionPdf && (
                  <PDFViewer pdfUrl={solutionPdf} title="PDF Answer Sheet" />
                )}
              </div>
            </div>
            <div className={styles.rightSidePanel}>
              {currentQuestion && (
                <EvaluationContainer
                  question={currentQuestion as Question}
                  onApprovalValidityChange={setApprovalValid}
                  onEvaluationChange={setEvaluationData}
                />
              )}
            </div>
              </>
            )}
          </div>
        </div>
      </div>
      <EvaluationFooter
        questionIndex={isReportView ? 0 : questionIndex}
        totalQuestions={questions.length}
        isSingleQuestionTask={singleQuestionTask}
        approvalValid={approvalValid}
        isSaving={isSaving || isSubmitting}
        isReportView={isReportView}
        showReportSummaryNav={isReportSummaryEnabled}
        onPrevious={goToPrevQuestion}
        onNextFromReport={goToNextFromReport}
        {...footerHandlers}
      />
      {renderModal()}
    </>
  );
}

export default EvaluationPage;
